import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions } from 'ngx-lottie';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    first,
    fromEvent,
    map,
    Observable,
    Subject,
    Subscription,
    takeUntil,
} from 'rxjs';
import { IFakeDetection, IPathAndDash } from 'src/app/models/defines';
import { IBasicLottieLayer, IDynamicLottieData } from 'src/app/models/lottie/lottie-defines';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { LottiePlayerService } from 'src/app/services/lottie-player.service';
import { VgApiService, VgMediaDirective } from '@videogular/ngx-videogular/core';
import { IComposedEditTakeConfigs, ISubtitleSettings, IVideoLottieCompose } from 'src/app/models/job/editor-defines';
import {
    IVideoLayer,
    VideoLayerPlaybackStrategyEnum,
    VideoLayerTypeEnum,
} from 'src/app/models/project/take/layers/video-layer-model';
import { UserService } from 'src/app/services/api/auth/user.service';
import { JassubService } from '../../services/jassub.service';
import { EditRoomStateService } from 'src/app/pages/private/dashboard/project/edit-room/edit-room-state.service';
import { v4 as uuidv4 } from 'uuid';
import { SubtitlesHelper } from 'sfl-subtitles-helper/dist/subtitles-helper';
import { IFont } from '../../models/configs/fonts.model';
import { FontsStoreService } from '../../services/state-management/configs/fonts-store.service';
import { bounceInOnEnterAnimation, bounceOutOnLeaveAnimation, flipInXOnEnterAnimation } from 'angular-animations';
import { AspectRatioEnum } from '../../models/project/edit/edit-model';
import { ITranscriptSentence } from '../../models/project/take/transcript-model';
import { VideoLayerUtils } from '../../models/project/take/layers/video-layer-utils';

interface IVgAndCanBePlayed {
    vgApi: VgApiService;
    canBePlayed: boolean;
    videoLayer: IVideoLayer;
}

interface ILottieLayerAndOptions {
    lottieLayer: IBasicLottieLayer;
    lottieData: IDynamicLottieData;
    options: AnimationOptions;
    isHidden$: BehaviorSubject<boolean>;
    startInFrame: number;
    endInFrame: number;
    totalFrames: number;
}

interface IComposedTake {
    id: string;
    lottiesLayersAndOptions: ILottieLayerAndOptions[];
    videoLayers: (IVideoLayer & { isHidden: boolean })[];
    durationInMilliseconds: number;
}

interface IAnimationAndSubscription {
    animationItem: AnimationItem;
    subscriptionToChanges: Subscription;
}

type VideoReadiness = {
    loadedMetadata: boolean;
    canPlayThrough: boolean;
    videoElement: HTMLVideoElement;
    videoLayer: IVideoLayer;
};

@Component({
    selector: 'app-lottie-video-composed',
    templateUrl: './lottie-video-composed.component.html',
    styleUrls: ['./lottie-video-composed.component.scss'],
    animations: [bounceInOnEnterAnimation(), bounceOutOnLeaveAnimation(), flipInXOnEnterAnimation()],
})
export class LottieVideoComposedComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    @ViewChildren(VgMediaDirective) vgMedias!: QueryList<VgMediaDirective>;
    @ViewChild('subtitleBlank') subtitleBlankRef!: ElementRef<HTMLVideoElement>;
    resizeTimeout: any;
    aspectRatioEnum = AspectRatioEnum;
    toDisplaySubtitles = false;
    @ViewChild('subtitlesCanvas', { static: true })
    canvas: ElementRef<HTMLCanvasElement>;
    subjectsToUnsubsribe: Subscription[] = [];
    @Input()
    displayVgControls: boolean = true;
    @Input()
    flipHorizontally: boolean = false;
    @Input()
    playOrPause: IFakeDetection;
    playOrPause$ = new BehaviorSubject<IFakeDetection>(null);
    /// Being rendered but not played .
    @Input()
    playNowOrWait: boolean = false;
    playNowOrWait$ = new BehaviorSubject<boolean>(false);
    /// To know if to freeze when play now or wait is being false, meaning we want to wait until palying but to see first frame
    @Input()
    freezeAtFirstFrame: boolean = true;
    @Input()
    notifyTime: boolean = true;
    @Input()
    showLoadingIndication: boolean = false;
    @Input()
    showPlayPauseOverlay: boolean = false;
    /// To get the interval time to notify on what time im on
    @Input()
    intervalNotifierTime: number = 0;
    timeUpdateSubscription: Subscription;
    /// To notify the parent which time im on
    @Output()
    currentVideoTime = new EventEmitter<number>();
    @Output()
    currentLottieTime = new EventEmitter<number>();
    @Input()
    timeToFreezeAfterFinishPlaying: number;
    /// Right now they are visible from the start (right now we only have 1 lottie per edit take)
    @Input('compose-configs')
    composeConfigs: IComposedEditTakeConfigs;
    composeConfigs$ = new BehaviorSubject<IComposedEditTakeConfigs>(null);
    /// Doing an array because a edit take might have few lotties,
    /// In the future, each lottie will have time to start in the edit take property,
    /// so we will know when to make them visible in the dom
    composesSubscriptions: Subscription;
    /**
     * special times - starting from the middle of edit take
     */
    @Input('start-time-on-running-edit-take')
    newStartTime: number;
    /**
     * special times - starting from the middle of Take, cutting a Take etc ..
     */
    @Input('special-end-time')
    endTime: number;
    @Input('totalTrackDuration')
    totalTrackDuration: number;
    @Input('currentTime')
    currentTime: number;
    @Input('navigateFromEditRoom')
    navigateFromEditRoom: boolean;
    @Input('toDisplayWatermark')
    toDisplayWatermark: boolean = false;
    @Input('toMuteVideo') toMuteVideo: boolean = false;
    @Output('is-loading')
    isLoading = new BehaviorSubject<boolean>(true);
    @Output('started-playing-edit-take')
    startedPlayingEditTake = new EventEmitter<string>();
    @Output('finished-playing-edit-take')
    finishedPlayingEditTake = new EventEmitter<string>();
    @Output() videoReady = new EventEmitter<string>();
    /**
     *  Request external playlist or tracks to play/pause all
     *  This is useful when the user the player is part of a sequence of videos
     */
    @Output() pauseRequested = new EventEmitter<void>();
    @Output() playRequested = new EventEmitter<void>();
    @Output() newMousePosition = new EventEmitter<number>();
    framesRate: number;
    fakeVideoUrl = '/files/platform/assets/fake.mp4';
    lottieLayersAndOptions$ = new BehaviorSubject<ILottieLayerAndOptions[]>(null);
    composedEditTakes$ = new BehaviorSubject<IComposedTake[]>(null);
    ///TODO: probably delete later
    composedEditTakes: IComposedTake[] = [];
    startInFrame$ = new BehaviorSubject<number>(null);
    endInFrame$ = new BehaviorSubject<number>(null);
    baseCdnUrl: string;
    areVideosReady$ = new BehaviorSubject<boolean>(false);
    canPlayVideosSubscription: Subscription[] = [];
    /// Currently Playing/Playable Lotties
    lottiePlayersMap = new Map<string, IAnimationAndSubscription>();
    /// the key is the lottie id, the value is the composed edit take id + lottie id (updated by the required Lotties that needs to be played)
    requiredUniqueLottiesIdMap = new Map<string, string>();
    areLottiesReady$ = new BehaviorSubject<boolean>(false);
    didLottieCleanup$ = new BehaviorSubject<boolean>(true);
    isFirst: boolean = true;
    viewHaveBeenInit$ = new BehaviorSubject<boolean>(false);
    /// It's done so the video won't show before the lottie does :)
    lottieStartedPromises: Promise<void>[] = [];
    ruleFramesToWaitForLottie = 2;

    wasScrubBarUsed: boolean = false;
    /// if there are lotties in the player and they start before frame by the waitinf rule, we wait for the animations to start play and then playing video
    isTimeVisible: boolean = true;
    isHidden = true;
    isBuffering = false;
    /// Videos that were force-muted by the decoder warm-up and need their mute state restored
    private warmUpMutedVideos = new Set<HTMLVideoElement>();
    watermarkUrl: string;
    /// When dragging from scrub bar we want to freeze the animation and not play it while dragging .
    draggingMode: boolean = false;
    /// if the user stopped the player and dragged, the video should stay paused, if the video was running, after drag it should keep running
    shouldRunAfterDragging: boolean;
    public currentTimeInSeconds: number = 0;
    currentTimeAfterTheTrimInSeconds: number = 0;
    public currentTimeWithTheTrimsInSeconds: number = 0;
    public currentTimeToDisplayInMilliSeconds: number = 0;
    isIOS: boolean = false;
    fonts: IFont[] = [];
    trimStart: number;
    trimEnd: number;
    totalDurationWithoutTrims: number;
    totalDurationWithTrimsInMilliseconds: number;

    protected readonly AspectRatioEnum = AspectRatioEnum;
    private videoLayerReadiness: Map<string, VideoReadiness> = new Map();
    private videoEndedSubscriptions: Subscription[] = [];
    private onDestroy$: Subject<boolean> = new Subject();

    constructor(
        private lottiePlayer: LottiePlayerService,
        private config: ConfigurationService,
        private jassubService: JassubService,
        private ngZone: NgZone,
        private userService: UserService,
        public editRoomStateService: EditRoomStateService,
        private fontsStoreService: FontsStoreService
    ) {
        this.framesRate = 30;
        this.baseCdnUrl = this.config.baseCdnUrl;
        this.isIOS = this.userService.isIOS;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.updateSubtitleColorsAndStyles();
        }, 300); // Adjust the debounce delay as needed
    }

    ngAfterViewInit(): void {
        this.viewHaveBeenInit$.next(true);
    }

    ngOnInit(): void {
        this.editRoomStateService.subtitleChanged$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.updateSubtitleColorsAndStyles();
            if (this.currentTimeInSeconds) {
                console.log(`Emitted new start from here`, this.currentTimeInSeconds);
                this.emitNewTime(this.currentTimeInSeconds + 0.001);
            }
        });
        this.subjectsToUnsubsribe.push(
            this.playOrPause$.pipe(takeUntil(this.onDestroy$)).subscribe((playOrPause) => {
                if (!playOrPause || !this.playNowOrWait) {
                    return;
                }
                this.playOrPause = playOrPause;

                if (playOrPause.isPlaying) {
                    if (!this.composeConfigs$.value) {
                        return;
                    }
                    const currentFrame = this.currentTimeAfterTheTrimInSeconds * 30;
                    const endFrame = this.endInFrame$.value;
                    /// secures that the video current time is not greater than the end time
                    /// If it does it means that we passed to another scene
                    // if (currentFrame > endFrame) {
                    //     this.handleVideoEnded();
                    //     return;
                    // }
                    new Promise<void>((resolve, reject) => {
                        for (const lottieAndSub of this.lottiePlayersMap.values()) {
                            const animationItem = lottieAndSub.animationItem;
                            console.log(`IM TRYING TO PLAY LOTTIE`, currentFrame, endFrame);
                            this.lottiePlayer.playOrFreezeAnimationBetweenFrames(
                                animationItem,
                                currentFrame,
                                endFrame,
                                true
                            );
                        }
                        return resolve();
                    });

                    new Promise<void>((resolve, rejecet) => {
                        this.vgMedias?.forEach((vgMedia) => {
                            vgMedia?.play();
                        });
                        const videoElement = this.subtitleBlankRef?.nativeElement;
                        videoElement?.play();
                        return resolve();
                    });
                }
                /// Pausing
                else {
                    new Promise<void>((resolve, reject) => {
                        for (const lottieAndSub of this.lottiePlayersMap.values()) {
                            this.lottiePlayer.pauseAnimation(lottieAndSub.animationItem);
                        }
                        return resolve();
                    });
                    new Promise<void>((resolve, reject) => {
                        this.vgMedias?.forEach((vgMedia) => {
                            vgMedia?.pause();
                        });
                        const videoElement = this.subtitleBlankRef?.nativeElement;
                        videoElement?.pause();
                        return resolve();
                    });
                }
            })
        );

        this.resetRelevantSubjects();
        this.composedEditTakes$.pipe(takeUntil(this.onDestroy$)).subscribe((composedTakes) => {
            this.composedEditTakes = composedTakes;
        });

        this.subjectsToUnsubsribe.push(
            this.composeConfigs$
                .pipe(
                    // The parent may re-feed a brand-new `composeConfigs` reference on every project-store
                    // emission (e.g. the storyboard rebuilds `composeConfigsFreezed` while planning/generating).
                    // Without this guard, each identical-content reference triggers a full teardown + reload
                    // (isHidden -> loading, refetch fonts, rebuild every video layer + lottie). Dedup by content
                    // so the player only rebuilds when the scene actually changed.
                    //
                    // NOTE: we deliberately do NOT use distinctUntilChanged here. Callers mutate nested
                    // config objects in place and then re-spread the top level (scene-setup.preview(),
                    // the dynamic-values editor). distinctUntilChanged holds the PREVIOUS emission by
                    // reference, so that in-place mutation retro-changes `prev` too and a real edit looks
                    // identical -> suppressed. Snapshotting to an immutable string AT EMISSION TIME avoids
                    // the aliasing: the baseline can't be mutated after the fact.
                    filter((cfg) => this.isComposeConfigsChanged(cfg)),
                    takeUntil(this.onDestroy$)
                )
                .subscribe(async (editTakeConfigs) => {
                    this.timeUpdateSubscription?.unsubscribe();

                    this.videoLayerReadiness.clear();
                    this.lottieStartedPromises = [];
                    this.isHidden = true;
                    const aspectRatio = editTakeConfigs.aspectRatio;
                    if (aspectRatio) {
                        switch (aspectRatio) {
                            case AspectRatioEnum._16x9:
                                this.watermarkUrl = '/files/platform/branding/watermarks/watermark_16-9.png';
                                break;
                            case AspectRatioEnum._1x1:
                                this.watermarkUrl = '/files/platform/branding/watermarks/watermark_1-1.png';
                                break;
                            case AspectRatioEnum._9x16:
                                this.watermarkUrl = '/files/platform/branding/watermarks/watermark_9-16.png';
                                break;
                        }
                    }
                    // this.requiredUniqueLottiesIdMap.clear();
                    if (!editTakeConfigs) {
                        this.composedEditTakes$.next(null);
                        return;
                    }
                    try {
                        this.fonts = await this.fontsStoreService.getFontsAsync();
                    } catch (error) {
                        this.fonts = [];
                    }
                    this.resetRelevantSubjects();
                    const builtEditTakes = editTakeConfigs.videoLottieConfigs.map((composeConfig) => {
                        if (!composeConfig.videoLayers || composeConfig.videoLayers.length === 0) {
                            const fakeId = uuidv4();
                            composeConfig.videoLayers = [
                                {
                                    id: fakeId,
                                    uploadPath: this.fakeVideoUrl,
                                    width: 100,
                                    height: 100,
                                    storeManagerId: uuidv4,
                                    defaultTrimEnd: 0,
                                    defaultTrimStart: 0,
                                    uinuqeId: uuidv4,
                                    videoPosition: {
                                        id: fakeId,
                                        position: 100,
                                        width: 100,
                                        height: 100,
                                        y: 0,
                                        x: 0,
                                    },
                                    toFlip: true,
                                    isMaster: true,
                                    playbackStrategy: null,
                                    type: VideoLayerTypeEnum.ASSET,
                                    isMuted: true,
                                    toUseSubtitles: true,
                                },
                            ];
                        }

                        const videoLayers = composeConfig.videoLayers;
                        const masterVideoLayer = videoLayers.find((vl) => vl.isMaster);
                        let duration = (editTakeConfigs.endTime - editTakeConfigs.startTime) * 1000;
                        if (masterVideoLayer) {
                            this.trimStart = masterVideoLayer.defaultTrimStart ?? 0;
                            this.trimEnd = masterVideoLayer.defaultTrimEnd ?? 0;
                            this.totalDurationWithoutTrims = masterVideoLayer.duration;
                            duration = duration - this.trimStart - this.trimEnd;
                            this.totalDurationWithTrimsInMilliseconds = duration;
                        }
                        // this.trimStart = editTakeConfigs.startTime * 1000;
                        // this.trimEnd = editTakeConfigs.endTime * 1000;
                        return this.buildComposedTake(composeConfig, duration);
                    });

                    const allLottiesAreSet = this.checkIfLottiesAreSyncdWhenNewConfigs();
                    this.areLottiesReady$.next(allLottiesAreSet);
                    if (this.isFirst) {
                        this.didLottieCleanup$.next(true);
                        this.isFirst = false;
                    } else {
                        this.didLottieCleanup$.next(allLottiesAreSet);
                    }
                    const validatedStartTime = this.validateTime(editTakeConfigs.startTime, 0);
                    const validatedEndTime = this.validateTime(editTakeConfigs.endTime, 1);
                    if (validatedStartTime && validatedEndTime) {
                        /// Reset the current times so it won't trigger events because if end time exists and i trigger
                        /// another start time, it will say true on conditions .
                        this.startInFrame$.next(null);
                        this.endInFrame$.next(null);

                        this.startInFrame$.next(this.convertTimeToFrames(editTakeConfigs.startTime, 0));

                        this.endInFrame$.next(this.convertTimeToFrames(editTakeConfigs.endTime, 1));
                    }
                    this.composedEditTakes$.next(builtEditTakes);
                    /// Checking if there are no video layers in our compositions that are currently playing
                    if (builtEditTakes.filter((editTake) => editTake.videoLayers.length !== 0).length === 0) {
                        this.areVideosReady$.next(true);
                    }
                })
        );
        const combinedSubjects$ = combineLatest([
            this.composedEditTakes$,
            this.startInFrame$,
            this.endInFrame$,
            this.areVideosReady$,
            this.areLottiesReady$,
            this.playNowOrWait$,
        ]);

        /// Not using here this.composedTakes because it might be triggered before initialize them to the current value .
        this.composesSubscriptions = combinedSubjects$
            .pipe(
                takeUntil(this.onDestroy$),
                map(async ([composedTakes, startInFrame, endInFrame, videosReady, lottiesReady, playNowOrWait]) => {
                    let lastParamChanged: string | null = null;
                    if (!playNowOrWait) {
                        lastParamChanged = 'playNowOrWait';
                    }
                    if (!composedTakes) {
                        lastParamChanged = 'composedTakes';
                    }
                    if (startInFrame === null) {
                        lastParamChanged = 'startInFrame';
                    }
                    if (endInFrame === null) {
                        lastParamChanged = 'endInFrame';
                    }
                    if (!videosReady) {
                        lastParamChanged = 'videosReady';
                    }
                    if (!lottiesReady) {
                        lastParamChanged = 'lottiesReady';
                    }
                    if (
                        !playNowOrWait ||
                        !composedTakes ||
                        startInFrame === null ||
                        endInFrame === null ||
                        !videosReady ||
                        !lottiesReady
                    ) {
                        this.lottiePlayersMap.forEach((animationItem) => {
                            // Use pause() instead of stop() to prevent visual reset to frame 0
                            animationItem.animationItem.pause();
                        });
                        return;
                    }
                    this.lottiePlayer.resetCurrentLoopCounter();

                    const activeLottiesPromise = new Promise<void>(async (lottieResolve, lottieReject) => {
                        composedTakes.map((composedTake) => {
                            composedTake.lottiesLayersAndOptions?.map(async (lottieLayerAndOptions) => {
                                const framesUntilPlay = lottieLayerAndOptions.startInFrame - startInFrame;

                                if (framesUntilPlay > 0) {
                                    /// Set frame time out to start display the lottie ! will change it when relevant

                                    lottieLayerAndOptions.isHidden$.next(true);
                                    const millisecondsUntilPlay = (framesUntilPlay / this.framesRate) * 1000;
                                    ///TODO: fix this because on pausing its not good (lotties that are inserted in the middle of the take)
                                    await new Promise((resolve) => setTimeout(resolve, millisecondsUntilPlay));
                                }
                                /// 'Activating'(show and plays the loaded lotties) by unhide them (triggers subject to play in the animationCreated)
                                console.log(
                                    `EMITTING HIDDEN TO FALSE`,
                                    lastParamChanged,
                                    startInFrame,
                                    lottieLayerAndOptions.startInFrame
                                );
                                lottieLayerAndOptions.isHidden$.next(false);
                            });
                        });
                        return lottieResolve();
                    });
                    /// Waiting for all basic lotties to be played and then we play the mp4 so it won't look emberssing
                    await activeLottiesPromise;
                    const activeVideosPromise = new Promise<void>(async (videoResolve, videoReject) => {
                        if (this.lottieStartedPromises.length > 0) {
                            await Promise.all(this.lottieStartedPromises);
                        }
                        if (this.videoLayerReadiness.size === 0) {
                            return videoResolve();
                        }
                        const timeInSeconds = this.convertFrameToSeconds(startInFrame);
                        const subtitleBlankVideo = this.subtitleBlankRef?.nativeElement;
                        if (subtitleBlankVideo) {
                            subtitleBlankVideo.currentTime = timeInSeconds;
                            if (this.playOrPause.isPlaying) {
                                subtitleBlankVideo.play().catch((err) => {
                                    console.warn('subtitleBlankRef failed to play:', err);
                                });
                            }
                        }
                        for (const [key, readiness] of this.videoLayerReadiness.entries()) {
                            const videoElement = readiness.videoElement;
                            if (!videoElement) {
                                continue;
                            }
                            const videoLayer = readiness.videoLayer;
                            const playbackStrategy = videoLayer.playbackStrategy;
                            let targetTime = timeInSeconds;
                            const calculatedTime = this.calculateVideoLayerTargetTime({
                                timeInSeconds,
                                videoDurationSeconds: videoElement.duration,
                                fallbackDurationMs: videoLayer.duration,
                                trimStartMs: videoLayer.defaultTrimStart ?? 0,
                                trimEndMs: videoLayer.defaultTrimEnd ?? 0,
                                playbackStrategy,
                            });

                            if (calculatedTime !== null) {
                                targetTime = calculatedTime;
                            }

                            // Restore mute state if it was force-muted by decoder warm-up
                            if (this.warmUpMutedVideos.has(videoElement)) {
                                videoElement.muted = false;
                                this.warmUpMutedVideos.delete(videoElement);
                            }

                            // Seek to time
                            videoElement.currentTime = targetTime;
                            if (readiness.canPlayThrough && this.playOrPause.isPlaying) {
                                try {
                                    await videoElement.play();
                                } catch (error) {
                                    console.warn(`Video ${key} failed to play:`, error);
                                }
                            }
                        }
                        // const defaultMedia = vgApiAndCanBePlayed.vgApi.getDefaultMedia();
                        //
                        // defaultMedia.subscriptions.canPlay.pipe(takeUntil(this.onDestroy$)).subscribe((error) => {
                        //     //  console.warn('WAITING PLAYING AGAIN ', error);
                        //     if (this.playOrPause.isPlaying) {
                        //         vgApiAndCanBePlayed.vgApi?.getDefaultMedia()?.play();
                        //         const videoElement = this.subtitleBlankRef?.nativeElement;
                        //         videoElement?.play();
                        //     }
                        // });

                        return videoResolve();
                    });
                    await activeVideosPromise;
                    /// Lottie hidden
                    this.isHidden = false;
                    /// There might times that we don't want to trigger that the take started in the beginning because
                    /// The take should be stopped.
                    this.startedPlayingEditTake.emit(this.composeConfigs$.value.id);
                    this.isLoading.next(false);
                    // if (!this.playOrPause || !this.playOrPause.isPlaying) {
                    //   // this.playOrPause$.next({
                    //   //   randomNumber: Math.random(),
                    //   //   isPlaying: false,
                    //   // });
                    // }
                })
            )
            .subscribe();
    }

    private hasEndedFired = false;

    handleVideoEnded() {
        // Guard against duplicate calls from both timeupdate and ended events
        if (this.hasEndedFired) {
            return;
        }
        this.hasEndedFired = true;
        this.isBuffering = false;
        this.videoEndedSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.finishedPlayingEditTake.emit(this.composeConfigs$.value.id);
        if (this.timeToFreezeAfterFinishPlaying) {
            if (this.composeConfigs.endTime > this.timeToFreezeAfterFinishPlaying) {
                this.emitNewTime(this.timeToFreezeAfterFinishPlaying);
            }
        }
    }

    onLoadedMetadata(event: Event, videoLayer: IVideoLayer, composeConfigsId: string, where: string): void {
        const videoElement = event.target as HTMLVideoElement;
        const key = this.getVideoLayerKey(videoLayer, composeConfigsId);
        console.log(`Metadata loaded for /${videoLayer.id}, duration=`, videoElement.duration);

        const state = this.videoLayerReadiness.get(key) || {
            loadedMetadata: false,
            canPlayThrough: false,
            videoElement,
            videoLayer: videoLayer,
        };

        state.loadedMetadata = true;
        state.videoElement = videoElement;
        this.videoLayerReadiness.set(key, state);

        this.checkAllVideoLayersReady(videoLayer, composeConfigsId);
    }

    onCanPlayThrough(event: Event, videoLayer: IVideoLayer, composeConfigsId: string): void {
        const videoElement = event.target as HTMLVideoElement;
        const key = this.getVideoLayerKey(videoLayer, composeConfigsId);

        const state = this.videoLayerReadiness.get(key) || {
            loadedMetadata: false,
            canPlayThrough: false,
            videoElement,
            videoLayer: videoLayer,
        };
        if (state.canPlayThrough) {
            return;
        }
        if (videoLayer.isMuted || this.toMuteVideo) {
            videoElement.muted = true;
        }
        const composedEdits = this.composedEditTakes$.value;
        if (!composedEdits || composedEdits.length === 0) {
            return;
        }
        const currentComposedEdit = composedEdits[0];
        const totalVideoLayers = currentComposedEdit.videoLayers;
        const onlyOneVideoLayer = totalVideoLayers.length === 1;
        state.canPlayThrough = true;
        state.videoElement = videoElement;
        this.videoLayerReadiness.set(key, state);
        const isMasterVideoLayer = videoLayer.isMaster || onlyOneVideoLayer;
        if (!isMasterVideoLayer) {
            fromEvent(videoElement, 'timeupdate')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(async () => {
                    const trimStart = (videoLayer.defaultTrimStart ?? 0) / 1000;
                    const trimEnd = (videoLayer.defaultTrimEnd ?? 0) / 1000;
                    const playbackStrategy = videoLayer.playbackStrategy;

                    await VideoLayerUtils.playbackMediaByPlaybackStrategyAsync(
                        trimStart,
                        trimEnd,
                        videoElement,
                        playbackStrategy,
                        key
                    );
                });
        }
        if (isMasterVideoLayer) {
            fromEvent(videoElement, 'play')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    for (const lottieAndSub of this.lottiePlayersMap.values()) {
                        if (lottieAndSub.animationItem.isPaused) {
                            this.lottiePlayer.playAnimation(lottieAndSub.animationItem);
                        }
                    }
                });
            fromEvent(videoElement, 'pause')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    for (const lottieAndSub of this.lottiePlayersMap.values()) {
                        this.lottiePlayer.pauseAnimation(lottieAndSub.animationItem);
                    }
                });
            let endInSeconds = 0;
            this.endInFrame$.pipe(takeUntil(this.onDestroy$)).subscribe((endFrame) => {
                if (!endFrame) {
                    return;
                }
                endInSeconds = this.convertFrameToSeconds(endFrame);
            });
            fromEvent(videoElement, 'timeupdate')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    // const currentTimeInSeconds = videoElement.currentTime;
                    const currentTimeInSeconds = videoElement.currentTime;
                    console.log(`This is me current time in seconds`, currentTimeInSeconds);
                    this.currentTimeInSeconds = currentTimeInSeconds;
                    if (this.wasScrubBarUsed) {
                        this.currentTimeAfterTheTrimInSeconds =
                            currentTimeInSeconds - videoLayer.defaultTrimStart / 1000;
                    } else {
                        this.currentTimeAfterTheTrimInSeconds = currentTimeInSeconds;
                    }
                    this.currentTimeToDisplayInMilliSeconds = currentTimeInSeconds;
                    this.currentTimeWithTheTrimsInSeconds = currentTimeInSeconds + videoLayer.defaultTrimStart / 1000;

                    // Emit time
                    this.currentVideoTime.emit(currentTimeInSeconds * 1000);

                    // Trim-end stop logic — trigger 300ms early for smoother scene transitions.
                    // DON'T pause the video here: let it keep playing its last frames
                    // while the next scene loads underneath for a seamless handoff.
                    const duration = this.totalDurationWithoutTrims ?? currentComposedEdit.durationInMilliseconds;
                    if (
                        this.currentTimeInSeconds * 1000 > duration - this.trimEnd - this.trimStart - 300 &&
                        this.playOrPause.isPlaying
                    ) {
                        this.handleVideoEnded();
                        return;
                    }

                    // End-of-scene logic — trigger 300ms early for smoother scene transitions
                    if (currentTimeInSeconds >= endInSeconds - 0.3) {
                        console.log(`I EMIT END SCENE222222!!!`);
                        this.handleVideoEnded();
                    }
                });
            fromEvent(videoElement, 'ended')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    console.log(`I EMIT END SCENE!!!`);
                    this.handleVideoEnded();
                });

            // Buffering indicator: show spinner when video stalls mid-playback
            fromEvent(videoElement, 'waiting')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    this.isBuffering = true;
                });
            fromEvent(videoElement, 'playing')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    this.isBuffering = false;
                });
            // Also clear buffering on pause (e.g. decoder warm-up does play→pause
            // which triggers 'waiting' but never reaches 'playing')
            fromEvent(videoElement, 'pause')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(() => {
                    this.isBuffering = false;
                });
        }
        this.checkAllVideoLayersReady(videoLayer, composeConfigsId);
    }

    ngOnDestroy(): void {
        this.composesSubscriptions?.unsubscribe();
        this.canPlayVideosSubscription?.forEach((subscription) => subscription.unsubscribe());
        this.subjectsToUnsubsribe?.forEach((subscription) => subscription.unsubscribe());
        this.timeUpdateSubscription?.unsubscribe();
        const uniqueId = this.getJassubUniqueId();
        if (uniqueId) {
            this.jassubService.destroyJassubtInstance(uniqueId);
        }
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    animationCreated(
        animationItem: AnimationItem,
        layerAndOptions: ILottieLayerAndOptions,
        composeTakeId: string,
        lottieId: string
    ) {
        /// if there are lotties in the player and they start before frame 2, we wait for the animations to start play and then playing video
        if (lottieId === '1' || lottieId === '2') {
            return;
        }
        this.subjectsToUnsubsribe.push(
            this.startInFrame$.pipe(first()).subscribe((startInFrame) => {
                if (isNaN(startInFrame)) {
                    return;
                }
                if (layerAndOptions.startInFrame > this.startInFrame$.value) {
                    return;
                }

                /// will be lower or equal to 0 if the start in frame is 0 probably, will be higher if start in frame is bigger and the lottie should be already on screen
                const lottiePlaying = new Promise<void>((resolve, reject) => {
                    animationItem.addEventListener('enterFrame', (event) => {
                        // this.editRoomStateService.currentTime$.next(
                        //   this.editRoomStateService.currentTime$.value +
                        //     event.totalTime
                        // );

                        // this.currentLottieTime.emit(event.totalTime);
                        if (event.currentTime - this.ruleFramesToWaitForLottie > 0) {
                            this.ngZone.run(() => {
                                resolve();
                            });
                            return animationItem.removeEventListener('enterFrame');
                        }
                    });
                });
                this.lottieStartedPromises.push(lottiePlaying);
            })
        );

        const lottieUniqueId = this.getLottieUniqueId(composeTakeId, lottieId);
        const animationAndSubscription: IAnimationAndSubscription = {
            animationItem: animationItem,
            subscriptionToChanges: null,
        };

        this.lottiePlayersMap.set(lottieUniqueId, animationAndSubscription);

        const lottiesCanBePlayed = this.checkIfLottiesAreSyncdAfterDelete();
        this.areLottiesReady$.next(lottiesCanBePlayed);

        const combinedSubjects$ = combineLatest([
            this.startInFrame$,
            this.endInFrame$,
            layerAndOptions.isHidden$,
            this.didLottieCleanup$,
            this.viewHaveBeenInit$,
        ]);

        if (animationItem.isLoaded) {
            this.handleLottieDomLoaded(animationAndSubscription, combinedSubjects$, lottieUniqueId);
        } else {
            animationItem.addEventListener('DOMLoaded', () => {
                this.handleLottieDomLoaded(animationAndSubscription, combinedSubjects$, lottieUniqueId);
            });
        }
    }

    requestPlayOrPause(toPlayOrPause: boolean) {
        if (toPlayOrPause) {
            if (this.navigateFromEditRoom) {
                this.playRequested.emit();
            } else {
                this.playOrPause$.next({ isPlaying: true, randomNumber: 2 });
            }
        } else {
            if (this.navigateFromEditRoom) {
                this.pauseRequested.emit();
            } else {
                this.playOrPause$.next({ isPlaying: false, randomNumber: 2 });
            }
        }
    }

    public updateComposedConfig(composeCOnfigs: IComposedEditTakeConfigs) {
        this.composeConfigs$.next(composeCOnfigs);
    }

    //** End of Vg player events */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['playNowOrWait']) {
            this.playNowOrWait$.next(this.playNowOrWait);
        }

        if (changes['composeConfigs']) {
            this.composeConfigs$.next(changes['composeConfigs'].currentValue);
        }
        /// Setting new options when input changes.
        if (changes['playOrPause']) {
            this.playOrPause$.next(this.playOrPause);
        }

        if (changes['newStartTime']) {
            if (!this.playNowOrWait) {
                return;
            }
            const validateStartTime = this.validateTime(this.newStartTime, 0);
            if (!validateStartTime) {
                console.warn(`Could not play new start time because he is not valid`);
                return;
            }
            console.log(`Emitted new start from here`, this.newStartTime);

            this.emitNewTime(this.newStartTime);
        }
        if (changes['endTime']) {
            const validateEndTime = this.validateTime(this.endTime, 1);
            console.log(`CHANGED 2 END TIMEEE, this.endTime`, this.endTime);
            this.endInFrame$.next(changes['endTime'].currentValue);
            // this.endInFrame = validateEndTime / this.framesRate;
            // this.endInFrame$.next(this.convertTimeToFrames(this.endTime, 1));
        }

        if (changes['videosSourcesOnTrack']) {
            this.timeUpdateSubscription?.unsubscribe();
            this.videoLayerReadiness.clear();
        }
    }

    public emitNewTime(newStartTime: number) {
        this.startInFrame$.next(this.convertTimeToFrames(newStartTime, 0));
        this.playOrPause$.next(this.playOrPause);
    }

    public emitNewComposeConfigs(newComposeConfig: IComposedEditTakeConfigs) {
        this.composeConfigs$.next(newComposeConfig);
    }

    /** Immutable content snapshot of the last compose configs that triggered a rebuild. */
    private lastComposeConfigsSerialized: string | null = null;

    /**
     * True when `cfg` differs in CONTENT from the last emission that rebuilt the player.
     * Serializing here — at emission time — captures an immutable baseline, so a later
     * in-place mutation of a previously-emitted object can't corrupt the comparison (the
     * aliasing trap that makes distinctUntilChanged drop real edits). If serialization
     * fails we conservatively allow the rebuild. `id` alone is unsafe as a key because it
     * is stable across text/color/media edits.
     */
    private isComposeConfigsChanged(cfg: IComposedEditTakeConfigs): boolean {
        let serialized: string | null;
        try {
            serialized = JSON.stringify(cfg);
        } catch {
            serialized = null;
        }
        if (serialized === null) {
            this.lastComposeConfigsSerialized = null;
            return true;
        }
        if (serialized === this.lastComposeConfigsSerialized) {
            return false;
        }
        this.lastComposeConfigsSerialized = serialized;
        return true;
    }

    dragStartedFromScrubBar() {
        this.shouldRunAfterDragging = this.playOrPause.isPlaying;
        if (this.playOrPause) {
            this.playOrPause$.next({
                isPlaying: false,
                randomNumber: Math.random(),
            });
        }

        this.draggingMode = true;
    }

    dragEndedFromScrubBar() {
        if (this.shouldRunAfterDragging) {
            this.playOrPause$.next({
                isPlaying: true,
                randomNumber: Math.random(),
            });
        }
        this.draggingMode = false;
    }

    newTimeFromScrubBar(timeInMillies: number) {
        if (this.navigateFromEditRoom) {
            this.newMousePosition.emit(timeInMillies);
        } else {
            timeInMillies = timeInMillies - this.trimStart;
            const timeInFrame = (timeInMillies * this.framesRate) / 1000;

            this.wasScrubBarUsed = true;
            this.startInFrame$.next(timeInFrame);
        }
    }

    animationDestroyed(id: string) {
        this.didLottieCleanup$.next(false);
        const animationAndSubscription = this.lottiePlayersMap.get(id);
        animationAndSubscription?.subscriptionToChanges?.unsubscribe();
        ///This happens after the combineLatest inside animationCreated is being triggered again, so we need to wait for deleting first.
        this.lottiePlayersMap.delete(id);
        this.deleteItemFromUniqueLottiesIdMap(id);

        const finishedCleanup = this.checkIfLottiesAreSyncdAfterDelete();
        this.didLottieCleanup$.next(finishedCleanup);
        console.log('LOTTIE REMOVED!', id);
        console.log(`DID CLEANUP LOTTIE?`, finishedCleanup);
    }

    getLottieUniqueId(composeTakeId: string, lottieId: string) {
        return composeTakeId + lottieId;
    }

    convertFrameToSeconds(frameNumber: number) {
        if (isNaN(frameNumber)) {
            return null;
        }
        return frameNumber / this.framesRate;
    }

    public setScrubTimeVisibilty(visible: boolean) {
        this.isTimeVisible = visible;
    }

    private getMasterVideoLayer() {
        const composedEdits = this.composedEditTakes$.value;
        if (!composedEdits || composedEdits.length === 0) {
            return;
        }
        const currentComposedEdit = composedEdits[0];
        const videoLayers = currentComposedEdit.videoLayers;
        return videoLayers.find((vl) => vl.isMaster);
    }

    private checkAllVideoLayersReady(videoLayer: IVideoLayer, composeConfigsId: string): void {
        const composedEdits = this.composedEditTakes$.value;
        if (!composedEdits || composedEdits.length === 0) {
            return;
        }
        const currentComposedEdit = composedEdits[0];
        const videoLayers = currentComposedEdit.videoLayers;
        const allReady = videoLayers.every((layer) => {
            // Fixed: use `layer` (loop variable) instead of `videoLayer` (parameter)
            const key = this.getVideoLayerKey(layer, composeConfigsId);
            const status = this.videoLayerReadiness.get(key);
            return status?.loadedMetadata && status?.canPlayThrough;
        });
        if (!allReady) {
            return;
        }
        const videoId = videoLayer.id;
        this.areVideosReady$.next(true);
        this.videoReady.emit(videoId);
        this.initializeSubtitles();

        // Warm up the browser's video decoder pipeline so .play() resolves
        // near-instantly at transition time. A quick play→pause cycle forces
        // the decoder to initialise without producing visible playback.
        this.warmUpVideoDecoders();
    }

    private warmUpVideoDecoders(): void {
        for (const [key, readiness] of this.videoLayerReadiness.entries()) {
            const video = readiness.videoElement;
            if (!video || !readiness.canPlayThrough) {
                continue;
            }
            // Only warm up if the video isn't already playing
            if (!video.paused) {
                continue;
            }
            // Remember if we had to force-mute so we can restore later
            if (!video.muted) {
                video.muted = true;
                this.warmUpMutedVideos.add(video);
            }
            video
                .play()
                .then(() => {
                    // Immediately pause — decoder is now primed
                    // video.pause();
                })
                .catch(() => {
                    // Autoplay blocked or element removed — safe to ignore
                });
        }
    }

    private getVideoLayerKey(videoLayer: IVideoLayer, layerId: string): string {
        const videoId = videoLayer.id;
        return `${videoId}_${layerId}`;
    }

    private createAssText(subtitlesConfigs: ISubtitleSettings, videoName: string) {
        // Filter out sentences where isHidden is true
        let filteredSentences: ITranscriptSentence[] = [];
        if (!subtitlesConfigs.configs.toActive) {
            filteredSentences = [];
        } else {
            filteredSentences = subtitlesConfigs.take.chosenTranscript.sentences.filter(
                (sentence) => !sentence.isHidden
            );
        }
        // const sentences = subtitlesConfigs.sentences;
        const strategyToPlay = subtitlesConfigs.configs.strategy;
        const styles = subtitlesConfigs.styles;
        const maxWords = subtitlesConfigs.maxWords;
        const assObj = SubtitlesHelper.transcriptToAssJSON(
            filteredSentences,
            strategyToPlay,
            styles,
            1920,
            1080,
            videoName ?? '',
            {
                maxWords: maxWords,
            }
        );
        const assText = SubtitlesHelper.stringify(assObj);
        return assText;
    }

    private handleLottieDomLoaded(
        animationAndSubscription: IAnimationAndSubscription,
        combinedSubjects$: Observable<[number, number, boolean, boolean, boolean]>,
        lottieUniqueId: string
    ) {
        let lastParamChanged: string | null = null;

        animationAndSubscription.subscriptionToChanges = combinedSubjects$
            .pipe(
                takeUntil(this.onDestroy$),
                map(([startFrame, endFrame, isHidden, didLottieCleanup, viewInit]) => {
                    // Track which parameter changed last
                    if (startFrame === null) {
                        lastParamChanged = 'startFrame';
                    }
                    if (endFrame === null) {
                        lastParamChanged = 'endFrame';
                    }
                    if (isHidden === true) {
                        lastParamChanged = 'isHidden';
                    }
                    if (didLottieCleanup === false) {
                        lastParamChanged = 'didLottieCleanup';
                    }
                    if (viewInit === false) {
                        lastParamChanged = 'viewInit';
                    }
                    if (!viewInit) {
                        console.log(`ONE OF THIS ARGUMENTS IS NULL`, viewInit);
                        return;
                    }
                    /// Might be  triggered on an animation item that we want to destroy but it was triggered before destroyed
                    if (!didLottieCleanup) {
                        console.log(`ONE OF THIS ARGUMENTS IS NULL`, didLottieCleanup);
                        console.warn('Did not cleanup lotties yet .');
                        return {
                            startFrame,
                            endFrame,
                            isHidden,
                            didLottieCleanup,
                        };
                    } else {
                        console.log(`IM CLEAN LOTTIE!!`);
                    }
                    /// Might be triggered after being destryoed.
                    const localAnimationItem = this.lottiePlayersMap.get(lottieUniqueId)?.animationItem;

                    if (!localAnimationItem) {
                        console.error(`AnimationItem with id ${lottieUniqueId} not found.`);
                        return {
                            startFrame,
                            endFrame,
                            isHidden,
                            didLottieCleanup,
                        };
                    }
                    if (startFrame === null || endFrame === null || isHidden) {
                        console.log(`ONE OF THIS ARGUMENTS IS NULL`, startFrame, endFrame, isHidden);
                        return {
                            startFrame,
                            endFrame,
                            isHidden,
                            didLottieCleanup,
                        };
                    }

                    // Log the last changed parameter here before clearing event listeners
                    console.log(`Last parameter to become null/false before clearing: ${lastParamChanged}`);
                    //if (!this.playOrPause.isPlaying) return;
                    /// If another frame was clicked on the same animation, we need to stop the current animation and his event listeneres. .
                    this.clearEventListeners(localAnimationItem);

                    if (!this.freezeAtFirstFrame) {
                        this.activateLottieAnimation(
                            localAnimationItem,
                            startFrame,
                            endFrame,
                            !this.draggingMode && this.playOrPause.isPlaying
                        );
                    } else {
                        /// Playing lottie with freezing (maybe waiting for something else to play it, depence on the fater component what he wants to do)
                        console.log(`Called activate lottie from here 2`, this.isLoading.value);
                        this.activateLottieAnimation(localAnimationItem, startFrame ?? 3, endFrame ?? 4, false);
                    }
                    // distinctUntilChanged();
                    return {
                        startFrame,
                        endFrame,
                        isHidden,
                        didLottieCleanup,
                    };
                })
            )
            .subscribe();
    }

    private deleteItemFromUniqueLottiesIdMap(specificKey: string): void {
        for (const [key, value] of this.requiredUniqueLottiesIdMap) {
            if (value === specificKey) {
                this.requiredUniqueLottiesIdMap.delete(key);
                return;
            }
        }
    }

    /**
     * When getting new configs, the lotties might be the same so we won't destroy any and the lotties are already ready .
     * @returns true if they do, false if not.
     */
    private checkIfLottiesAreSyncdWhenNewConfigs(): boolean {
        const requiredLottiesEntries = Array.from(this.requiredUniqueLottiesIdMap.keys());
        for (const uniqueLottieId of requiredLottiesEntries) {
            // Check if the value exists as a key in the second map
            if (!this.lottiePlayersMap.has(uniqueLottieId)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Checks whether the currently playing/playable Lotties match the updated ones that need to be played,
     * allowing mismatches up to the difference in length between the maps.
     * @returns true if they do, false if there are more mismatches.
     */
    private checkIfLottiesAreSyncdAfterDelete(): boolean {
        const requiredLottiesEntries = Array.from(this.requiredUniqueLottiesIdMap.entries());
        const lottiePlayersEntries = Array.from(this.lottiePlayersMap.entries());

        const lengthDifference = Math.abs(requiredLottiesEntries.length - lottiePlayersEntries.length);

        let mismatches = 0;

        // Iterate through the entries of the first map
        for (const [key, value] of requiredLottiesEntries) {
            // Check if the value exists as a key in the second map
            if (!this.lottiePlayersMap.has(value)) {
                mismatches++;

                // If the number of mismatches is greater than the allowed difference, return false
                if (mismatches > lengthDifference) {
                    return false;
                }
            }
        }

        // If the number of mismatches is less than or equal to the allowed difference, return true
        return true;
    }

    private resetRelevantSubjects() {
        this.areVideosReady$.next(false);
        this.hasEndedFired = false;
        this.isBuffering = false;
        this.warmUpMutedVideos.clear();
    }

    private buildComposedTake(composeConfig: IVideoLottieCompose, duration: number) {
        const { basicLottieLayersOnTrack, videoLayers, lottieData } = composeConfig;

        const lottieLayerAndOptions: ILottieLayerAndOptions[] = basicLottieLayersOnTrack.map((lottieLayer) => {
            const options = this.createLottieOptions(this.baseCdnUrl + lottieLayer.lottieJsonPath);
            /// Setting the lotties that are on track unique ids to validate after with the current acivated lotties

            const lottieId = lottieLayer.lottieId;
            const lottieUniqueId = this.getLottieUniqueId(composeConfig.id, lottieId);
            this.requiredUniqueLottiesIdMap.set(lottieId, lottieUniqueId);

            const lottieTrimStart = lottieLayer.trimStart ?? 0;
            const lottieTrimEnd = lottieLayer.trimEnd ?? 0;
            const lottieLayerAndOptions: ILottieLayerAndOptions = {
                lottieLayer: lottieLayer,
                startInFrame: (lottieLayer.startTime * this.framesRate + lottieTrimStart) / 1000,
                endInFrame: (lottieLayer.endTime * this.framesRate - lottieTrimEnd ?? 0) / 1000,
                totalFrames:
                    ((lottieLayer.endTime - lottieLayer.startTime - lottieTrimStart - lottieTrimEnd) *
                        this.framesRate) /
                    1000,
                options: options,
                lottieData: lottieData,
                isHidden$: new BehaviorSubject<boolean>(true),
            };
            return lottieLayerAndOptions;
        });

        const updatedVideoLayers = videoLayers?.map((videoLayer) => {
            let dashPath: string;
            if (videoLayer.dashPath) {
                dashPath = decodeURIComponent(videoLayer.dashPath);
            }
            let uploadPath: string;
            if (videoLayer.uploadPath) {
                uploadPath = decodeURIComponent(videoLayer.uploadPath);
            }
            const pathAndDash: IPathAndDash = {
                uploadPath: uploadPath ?? null,
                dashPath: dashPath ?? null,
            };

            const updatedVideoLayer: IVideoLayer & { isHidden: boolean } = {
                ...videoLayer,
                ...pathAndDash,
                isHidden: VideoLayerUtils.getIsBackgroundPosition(videoLayer.videoPosition),
            };
            return updatedVideoLayer;
        });

        VideoLayerUtils.sortVideoLayersByPosition(updatedVideoLayers);

        const composedTake: IComposedTake = {
            id: composeConfig.id,
            lottiesLayersAndOptions: lottieLayerAndOptions,
            videoLayers: updatedVideoLayers ?? [],
            durationInMilliseconds: duration,
        };
        return composedTake;
    }

    private clearEventListeners(animationItem: AnimationItem) {
        // Use pause() instead of stop() to prevent flashing to frame 0 during seeks
        animationItem.pause();
        animationItem.removeEventListener('complete');
        animationItem.removeEventListener('loopComplete');
        animationItem.removeEventListener('DOMLoaded');
    }

    /**
     *
     * @param animationItem
     * @param startFrame
     * @param endFrame
     * @param toFreeze - true for playing, false for freezing
     */
    private activateLottieAnimation(
        animationItem: AnimationItem,
        startFrame: number,
        endFrame: number,
        toFreeze: boolean
    ): void {
        // animationItem.addEventListener('DOMLoaded', () => {

        this.lottiePlayer.playOrFreezeAnimationBetweenFrames(animationItem, startFrame, endFrame, toFreeze);

        //});
    }

    private createLottieOptions(lottiePath: string) {
        const options: AnimationOptions = {
            path: decodeURIComponent(lottiePath),
            autoplay: false,
            loop: false,
            assetsPath: `${this.baseCdnUrl}`,
        };
        return options;
    }
    private calculateVideoLayerTargetTime(params: {
        timeInSeconds: number;
        videoDurationSeconds: number | undefined;
        fallbackDurationMs?: number;
        trimStartMs?: number;
        trimEndMs?: number;
        playbackStrategy: VideoLayerPlaybackStrategyEnum;
    }): number | null {
        const {
            timeInSeconds,
            videoDurationSeconds,
            fallbackDurationMs,
            trimStartMs = 0,
            trimEndMs = 0,
            playbackStrategy,
        } = params;

        // Resolve duration
        let duration = videoDurationSeconds;

        if (!isFiniteDuration(duration) && fallbackDurationMs) {
            duration = fallbackDurationMs / 1000;
        }

        if (!isFiniteDuration(duration) || duration <= 0) {
            return null;
        }

        const trimStart = trimStartMs / 1000;
        const trimEnd = trimEndMs / 1000;

        const effectiveStart = trimStart;
        const effectiveEnd = Math.max(0, duration - trimEnd);
        const effectiveDuration = effectiveEnd - effectiveStart;

        // 🚨 Invalid trim → no playable segment
        if (effectiveDuration <= 0) {
            // Clamp to a valid time inside the video
            return Math.max(0, Math.min(effectiveStart, duration - 0.1));
        }

        if (timeInSeconds >= effectiveDuration) {
            if (playbackStrategy === VideoLayerPlaybackStrategyEnum.LAST_FRAME_FREEZE) {
                return Math.max(0, effectiveEnd - 0.1);
            }

            if (playbackStrategy === VideoLayerPlaybackStrategyEnum.LOOP_VIDEO) {
                return effectiveStart + (timeInSeconds % effectiveDuration);
            }
        }

        // Normal playback
        return effectiveStart + timeInSeconds;
    }

    private convertTimeToFrames(time: number, equalAndAboveTo: number) {
        return !isNaN(time) && time >= equalAndAboveTo
            ? Math.max(time * this.framesRate, this.ruleFramesToWaitForLottie + 1)
            : null;
    }

    private validateTime(time: number, equalAndAboveTo: number): boolean {
        return !isNaN(time) && typeof time === 'number' && time >= equalAndAboveTo;
    }

    private getJassubUniqueId() {
        const composeConfigs = this.composeConfigs$.value;
        if (!composeConfigs) {
            return null;
        }
        return composeConfigs.id;
    }

    private updateSubtitleColorsAndStyles(): void {
        const composeConfigs = this.composeConfigs$.value;
        if (!composeConfigs?.subtitlesConfigs?.styles) {
            return;
        }
        const { uniqueId, assText } = this.getDataForJassub();

        this.jassubService.updateSubtitlesWithContent(assText, uniqueId);
    }

    private initializeSubtitles() {
        // const defaultMedia = vgApi?.ge();
        // console.log('defaultMedia', defaultMedia);
        const composeConfigs = this.composeConfigs$.value;
        if (!composeConfigs || !composeConfigs.subtitlesConfigs?.take?.chosenTranscript?.sentences.length) {
            this.toDisplaySubtitles = false;
            return;
        }
        this.toDisplaySubtitles = true;

        if (!this.canvas?.nativeElement) {
            return;
        }
        const videoElement = this.subtitleBlankRef?.nativeElement;
        if (!videoElement) {
            return;
        }
        const { uniqueId, assText, fonts } = this.getDataForJassub();

        const canvasWidth = this.canvas.nativeElement.clientWidth;
        const canvasHeight = this.canvas.nativeElement.clientHeight;
        const stagePosition = this.composeConfigs.videoLottieConfigs[0].videoLayers[0].videoPosition;
        const prescaleFactor = ((stagePosition.height / 100) * canvasHeight) / 1080;
        console.log(`Canvas`, this.canvas?.nativeElement);
        this.jassubService.initializeCanvasJassub(
            this.canvas.nativeElement,
            videoElement,
            uniqueId,
            fonts,
            prescaleFactor,
            assText,
            'content'
        );

        const fontUrls: string[] = [];
        for (const font of this.fonts) {
            const regualrFont = this.baseCdnUrl + font.regularUrl;
            if (font.italicUrl) {
                const italicFont = this.baseCdnUrl + font.italicUrl;
                fontUrls.push(italicFont);
            }
            fontUrls.push(regualrFont);
        }

        this.jassubService.addFontToJassub(fontUrls, uniqueId);
    }

    private getDataForJassub() {
        const uniqueId = this.getJassubUniqueId();
        const assText = this.getAssText(uniqueId);
        const fonts = this.composeConfigs.subtitlesConfigs.fonts;
        return { uniqueId, assText, fonts };
    }

    private getAssText(uniqueId: string) {
        const composeConfigs = this.composeConfigs$.value;
        // this.updateSubtitlesFonts();

        // const updatedConfigs = this.updateStylesWithColors(composeConfigs);

        if (!composeConfigs) {
            return;
        }

        // Because the styles are being updated in the edit room so the compose configs
        return this.createAssText(composeConfigs.subtitlesConfigs, uniqueId);
    }
}

function isFiniteDuration(val: number): boolean {
    return typeof val === 'number' && isFinite(val) && val > 0;
}
